import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ═══════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════ */

export type GroceryListing = {
  listingId:   string;
  brandId:     string;
  hostId:      string;

  name?:        string;
  description?: string;
  categoryId?:  number | null;   // maps to businessType ids (1–12)
  subcategoryId?: number | null;

  /* pricing */
  rate?:        number;          // price per unit
  unit?:        string;          // e.g. "Per kg", "Per piece", "Per tray"
  minOrder?:    number;          // minimum order quantity

  /* variants */
  variants?:    Record<string, any>; // e.g. { "Organic": {...}, "Fresh": {...} }
  variant?:     string;              // active variant name

  /* stock */
  stock?:       number;
  available?:   boolean;

  /* media */
  photos?:      string[];
  videoUrl?:    string;

  /* grocery-specific */
  isOrganic?:   boolean;
  isFreeRange?: boolean;
  isFresh?:     boolean;        // same-day harvest
  expiryDate?:  string | null;  // ISO string
  origin?:      string;         // farm / region of origin
  weight?:      number;         // weight in grams (for display)
  weightUnit?:  string;         // "g" | "kg" | "lb"
  allergens?:   string[];
  nutritionInfo?: Record<string, string>;

  /* status */
  isPublished:  boolean;
  isFlagged:    boolean;
  isComplete:   boolean;
  isReviewed:   boolean;
  isDraft?:     boolean;

  /* sold count */
  sold?:        number;

  joinDate:     string;
  createdAt?:   any;
  updatedAt?:   any;

  [key: string]: any;
};

type GroceriesState = {
  listings:          GroceryListing[];
  filteredListings:  GroceryListing[];
  currentListing:    GroceryListing | null;

  filters: {
    city:        string | null;
    country:     string | null;
    categoryId:  number | null;
  };

  /* brand-scoped (for GroceriesBrand page) */
  brandListings:        GroceryListing[];
  brandListingsId:      string | null;
  brandListingsLoading: boolean;

  unsubscribe?:      () => void;
  brandUnsubscribe?: () => void;

  loading: boolean;
  error:   string | null;
};

const initialState: GroceriesState = {
  listings:          [],
  filteredListings:  [],
  currentListing:    null,
  filters: { city: null, country: null, categoryId: null },
  brandListings:        [],
  brandListingsId:      null,
  brandListingsLoading: false,
  unsubscribe:      undefined,
  brandUnsubscribe: undefined,
  loading: false,
  error:   null,
};

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseListing = (
  id: string,
  raw: Record<string, any>
): GroceryListing => {
  const { createdAt, updatedAt, ...safeRaw } = raw;
  return {
    listingId:    id,
    brandId:      raw.brandId      ?? "",
    hostId:       raw.hostId       ?? "",

    name:         raw.name         ?? "",
    description:  raw.description  ?? "",
    categoryId:   raw.categoryId   ?? null,
    subcategoryId:raw.subcategoryId?? null,

    rate:         raw.rate         ?? 0,
    unit:         raw.unit         ?? "",
    minOrder:     raw.minOrder     ?? 1,

    variants:     raw.variants     ?? {},
    variant:      raw.variant      ?? "",

    stock:        raw.stock        ?? 0,
    available:    raw.available    ?? true,

    photos:       raw.photos       ?? [],
    videoUrl:     raw.videoUrl     ?? "",

    isOrganic:    raw.isOrganic    ?? false,
    isFreeRange:  raw.isFreeRange  ?? false,
    isFresh:      raw.isFresh      ?? false,
    expiryDate:   raw.expiryDate   ?? null,
    origin:       raw.origin       ?? "",
    weight:       raw.weight       ?? null,
    weightUnit:   raw.weightUnit   ?? "g",
    allergens:    raw.allergens    ?? [],
    nutritionInfo:raw.nutritionInfo?? {},

    isPublished:  raw.isPublished  ?? false,
    isFlagged:    raw.isFlagged    ?? false,
    isComplete:   raw.isComplete   ?? false,
    isReviewed:   raw.isReviewed   ?? false,
    isDraft:      raw.isDraft      ?? true,

    sold:         raw.sold         ?? 0,

    joinDate:     raw.joinDate     ?? new Date().toISOString(),
    createdAt:    toISO(createdAt),
    updatedAt:    toISO(updatedAt),

    ...safeRaw,
  };
};

/* ═══════════════════════════════════════════════
   UPSERT  (create or update a grocery listing)
═══════════════════════════════════════════════ */

export const upsertGroceryListing = createAsyncThunk<
  GroceryListing,
  {
    listingId?: string;
    data: Partial<Omit<GroceryListing, "listingId" | "brandId" | "hostId" | "joinDate" | "createdAt" | "updatedAt">>;
  }
>(
  "groceries/upsert",
  async ({ listingId, data }, { rejectWithValue }) => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("User not authenticated");

      /* resolve brandId from commercePartners */
      const partnerSnap = await getDocs(
        query(collection(db, "commercePartners"), where("userId", "==", user.uid))
      );
      if (partnerSnap.empty) throw new Error("No commerce partner found for this user.");
      const brandId = partnerSnap.docs[0].id;

      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      );

      const baseData = {
        ...cleanData,
        brandId,
        hostId:    user.uid,
        updatedAt: serverTimestamp(),
      };

      /* ── update existing ── */
      if (listingId) {
        const refDoc = doc(db, "groceries", listingId);
        await updateDoc(refDoc, baseData);
        const snap = await getDoc(refDoc);
        return parseListing(snap.id, snap.data() || {});
      }

      /* ── create new ── */
      const payload = {
        ...baseData,
        joinDate:    new Date().toISOString(),
        createdAt:   serverTimestamp(),
        sold:        0,
        isPublished: cleanData.isPublished ?? false,
        isDraft:     cleanData.isDraft ?? !(cleanData.isPublished ?? false),
        isFlagged:   false,
        isComplete:  false,
        isReviewed:  false,
      };

      const newDoc = await addDoc(collection(db, "groceries"), payload);
      await updateDoc(newDoc, { listingId: newDoc.id });

      const snap = await getDoc(newDoc);
      return parseListing(newDoc.id, snap.data() || {});
    } catch (err: any) {
      return rejectWithValue(err.message || "upsert failed");
    }
  }
);

/* ═══════════════════════════════════════════════
   LISTENERS
═══════════════════════════════════════════════ */

/* All published listings (marketplace feed) — chunked by brandId */
export const listenToPublishedGroceries =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().groceries;
    if (unsubscribe) return; // already listening

    const brandsUnsub = onSnapshot(
      query(collection(db, "commercePartners"), where("isPublished", "==", true)),
      (brandSnap) => {
        const brandIds = brandSnap.docs.map((d) => d.id);

        if (!brandIds.length) {
          dispatch(setListings([]));
          return;
        }

        /* chunk by 10 (Firestore "in" limit) */
        const chunks: string[][] = [];
        for (let i = 0; i < brandIds.length; i += 10)
          chunks.push(brandIds.slice(i, i + 10));

        let allListings: GroceryListing[] = [];
        const productListeners: (() => void)[] = [];

        chunks.forEach((chunk) => {
          const unsub = onSnapshot(
            query(
              collection(db, "groceries"),
              where("isPublished", "==", true),
              where("brandId", "in", chunk)
            ),
            (snap) => {
              const fresh = snap.docs.map((d) => parseListing(d.id, d.data()));
              allListings = [
                ...allListings.filter(
                  (l) => !fresh.some((f) => f.listingId === l.listingId)
                ),
                ...fresh,
              ];
              dispatch(setListings(allListings));
            }
          );
          productListeners.push(unsub);
        });

        dispatch(
          setUnsubscribe(() => {
            brandsUnsub();
            productListeners.forEach((u) => u());
          })
        );
      }
    );
  };

/* Single listing */
export const listenToGroceryListing =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;

    const { unsubscribe } = getState().groceries;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const unsub = onSnapshot(doc(db, "groceries", listingId), (snap) => {
      dispatch(
        setCurrentListing(snap.exists() ? parseListing(snap.id, snap.data()!) : null)
      );
    });

    dispatch(setUnsubscribe(unsub));
  };

/* All listings for a single host (seller dashboard) */
export const listenToHostGroceries =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().groceries;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const unsub = onSnapshot(
      query(collection(db, "groceries"), where("hostId", "==", hostId)),
      (snap) => dispatch(setListings(snap.docs.map((d) => parseListing(d.id, d.data()))))
    );

    dispatch(setUnsubscribe(unsub));
  };

/* Brand-scoped listener — used in GroceriesBrand page */
export const listenToBrandGroceries =
  (brandId: string) =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {
    if (!brandId) return () => {};

    dispatch(setBrandListingsLoading(true));
    dispatch(setBrandListingsId(brandId));

    const unsub = onSnapshot(
      query(
        collection(db, "groceries"),
        where("brandId",    "==", brandId),
        where("isPublished","==", true)
      ),
      (snap) => {
        dispatch(setBrandListings(snap.docs.map((d) => parseListing(d.id, d.data()))));
        dispatch(setBrandListingsLoading(false));
      },
      (err) => {
        dispatch(setError(err.message));
        dispatch(setBrandListingsLoading(false));
      }
    );

    dispatch(setBrandUnsubscribe(unsub));
    return unsub;
  };

/* Stop brand listener */
export const stopBrandGroceries =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { brandUnsubscribe } = getState().groceries;
    if (brandUnsubscribe) {
      brandUnsubscribe();
      dispatch(setBrandUnsubscribe(undefined));
      dispatch(setBrandListingsId(null));
      dispatch(clearBrandListings());
    }
  };

/* Category filter listener */
export const listenToGroceriesByCategory =
  (categoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;

    const { unsubscribe } = getState().groceries;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const unsub = onSnapshot(
      query(
        collection(db, "groceries"),
        where("isPublished", "==", true),
        where("categoryId",  "==", categoryId)
      ),
      (snap) => {
        const items = snap.docs.map((d) => parseListing(d.id, d.data()));
        if (!items.length) {
          dispatch(listenToPublishedGroceries());
          return;
        }
        dispatch(setListings(items));
      },
      (err) => dispatch(setError(err.message))
    );

    dispatch(setUnsubscribe(unsub));
  };

/* ═══════════════════════════════════════════════
   LOCAL FILTER  (by country, same pattern as productSlice)
═══════════════════════════════════════════════ */

export const filterGroceriesByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!country) {
      dispatch(setFilteredListings(getState().groceries.listings));
      return;
    }

    try {
      const partnerSnap = await getDocs(
        query(collection(db, "commercePartners"), where("address.country", "==", country))
      );
      const localBrandIds = partnerSnap.docs.map((d) => d.id);

      if (!localBrandIds.length) { dispatch(setFilteredListings([])); return; }

      const filtered = getState().groceries.listings.filter((l) =>
        localBrandIds.includes(l.brandId)
      );

      dispatch(setFilteredListings(filtered));
    } catch (err: any) {
      dispatch(setError(err.message));
    }
  };

/* ═══════════════════════════════════════════════
   MEDIA
═══════════════════════════════════════════════ */

export const upsertGroceryMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; files: File[]; type: "photo" | "video" }
>(
  "groceries/upsertMedia",
  async ({ listingId, files, type }, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    let id = listingId;
    const uploaded: string[] = [];

    for (const file of files) {
      const folder = type === "photo" ? "photos" : "videos";
      const path   = `groceries/${id || "new"}/${folder}/${file.name}`;
      const sRef   = ref(storage, path);
      await uploadBytes(sRef, file);
      uploaded.push(await getDownloadURL(sRef));
    }

    if (!id) {
      const created = await dispatch(
        upsertGroceryListing({
          data: {
            photos:   type === "photo" ? uploaded : [],
            videoUrl: type === "video" ? uploaded[0] : "",
          },
        })
      ).unwrap();
      id = created.listingId;
    } else {
      await dispatch(
        upsertGroceryListing({
          listingId: id,
          data: type === "photo"
            ? { photos: uploaded }
            : { videoUrl: uploaded[0] },
        })
      );
    }

    return { listingId: id!, downloadURLs: uploaded };
  }
);

export const deleteGroceryPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>(
  "groceries/deletePhoto",
  async ({ listingId, photoUrl }) => {
    const refDoc = doc(db, "groceries", listingId);
    const snap   = await getDoc(refDoc);
    if (!snap.exists()) throw new Error("Listing not found");

    const photos: string[] = snap.data()?.photos ?? [];
    await updateDoc(refDoc, { photos: photos.filter((p) => p !== photoUrl) });

    if (photoUrl.includes("/o/")) {
      const encoded = photoUrl.split("/o/")[1].split("?")[0];
      await deleteObject(ref(storage, decodeURIComponent(encoded)));
    }

    return photoUrl;
  }
);

/* ═══════════════════════════════════════════════
   DELETE
═══════════════════════════════════════════════ */

export const deleteGroceryListing = createAsyncThunk<
  { listingId: string },
  string
>("groceries/delete", async (listingId) => {
  await deleteDoc(doc(db, "groceries", listingId));
  return { listingId };
});

/* ═══════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════ */

const groceriesSlice = createSlice({
  name: "groceries",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<GroceryListing[]>) => {
      state.listings = action.payload;
    },
    setFilteredListings: (state, action: PayloadAction<GroceryListing[]>) => {
      state.filteredListings = action.payload;
    },
    setCurrentListing: (state, action: PayloadAction<GroceryListing | null>) => {
      state.currentListing = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<{ city?: string | null; country?: string | null; categoryId?: number | null }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = { city: null, country: null, categoryId: null };
      state.filteredListings = state.listings;
    },
    /* brand-scoped */
    setBrandListings: (state, action: PayloadAction<GroceryListing[]>) => {
      state.brandListings = action.payload;
    },
    clearBrandListings: (state) => {
      state.brandListings = [];
    },
    setBrandListingsId: (state, action: PayloadAction<string | null>) => {
      state.brandListingsId = action.payload;
    },
    setBrandListingsLoading: (state, action: PayloadAction<boolean>) => {
      state.brandListingsLoading = action.payload;
    },
    setBrandUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.brandUnsubscribe = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertGroceryListing.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(upsertGroceryListing.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.listings.findIndex(
          (l) => l.listingId === action.payload.listingId
        );
        if (idx !== -1) state.listings[idx] = action.payload;
        else state.listings.push(action.payload);
      })
      .addCase(upsertGroceryListing.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      })
      .addCase(deleteGroceryListing.fulfilled, (state, action) => {
        state.listings = state.listings.filter(
          (l) => l.listingId !== action.payload.listingId
        );
        state.filteredListings = state.filteredListings.filter(
          (l) => l.listingId !== action.payload.listingId
        );
      });
  },
});

export const {
  setListings,
  setFilteredListings,
  setCurrentListing,
  setUnsubscribe,
  setError,
  setFilters,
  resetFilters,
  setBrandListings,
  clearBrandListings,
  setBrandListingsId,
  setBrandListingsLoading,
  setBrandUnsubscribe,
} = groceriesSlice.actions;

export default groceriesSlice.reducer;
